import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Loader, fmt, fmtDateTime, StatusBadge, EmptyState } from "@/components/shared";
import { ShareStat, Reveal } from "@/components/ShareVisuals";

export default function MyShares() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/shares/me").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  if (data === null) return <Loader label="Loading your shares..." />;
  const h = data?.holding || {}, info = data?.info || {}, purchases = data?.purchases || [];

  return (
    <div className="space-y-6" data-testid="my-shares-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Ownership</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">My Shares</h1>
          <p className="text-sm text-slate-400 mt-2">Confirmed holdings and your full share purchase history.</p>
        </div>
        <Link to="/shares-portal" data-testid="my-shares-buy-btn" className="btn-glow px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950 inline-flex items-center gap-2 shrink-0">Buy Shares <ArrowRight className="w-4 h-4" /></Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ShareStat label="Confirmed Shares" value={h.shares} testId="my-shares-total" accent="text-cyan-300" />
        <ShareStat label="Share Price" value={info.share_price} decimals={2} suffix="USDT" testId="my-shares-price" />
        <ShareStat label="Total Purchase Value" value={h.total_value} decimals={2} suffix="USDT" testId="my-shares-value" />
        <ShareStat label="Pending Shares" value={h.pending_shares} testId="my-shares-pending" accent="text-amber-300" />
      </div>

      <Reveal className="glass rounded-2xl p-6">
        <h3 className="font-heading text-lg text-white mb-5">Purchase History</h3>
        {purchases.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="text-left text-slate-500 border-b border-white/5">
                {["Reference", "Shares", "Price", "Total (USDT)", "Source", "Status", "Date"].map((x) => <th key={x} className="pb-3 font-medium">{x}</th>)}
              </tr></thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 last:border-0" data-testid={`purchase-row-${p.id}`}>
                    <td className="py-3"><Link to={`/share-confirmation/purchase/${p.id}`} className="font-mono text-xs text-cyan-400 hover:underline">{p.id.slice(0, 8)}</Link></td>
                    <td className="py-3 font-mono text-white">{p.quantity}</td>
                    <td className="py-3 font-mono text-slate-300">{fmt(p.share_price)}</td>
                    <td className="py-3 font-mono text-white">{fmt(p.total)}</td>
                    <td className="py-3 text-slate-300 capitalize">{p.source}</td>
                    <td className="py-3"><StatusBadge status={p.status} />{p.status === "rejected" && p.admin_note && <p className="text-xs text-red-400 mt-1">{p.admin_note}</p>}</td>
                    <td className="py-3 text-slate-500">{fmtDateTime(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={PieChart} title="No shares yet" subtitle="Purchase your first company shares from the Company Shares page." action={<Link to="/shares-portal" className="btn-glow px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950">Go to Company Shares</Link>} />}
      </Reveal>
    </div>
  );
}
